import * as React from 'react';
import {FormComponentProps} from "antd/lib/form";
import {
  Attachment,
  ClientSummary,
  COST_BEARER_RENTER,
  Item,
  MaintenanceHistory,
  MaintenanceHistoryItemEntry,
  Site
} from "../api/dtos/dtos";
import {Button, Form, Modal} from "antd";
import moment from 'moment';
import locales from "../locales";
import InputFormItem from "../common/form-items/InputFormItem";
import SubmitCancelFormItem from "../common/form-items/SubmitCancelFormItem";
import DatePickerFormItem from "../common/form-items/DatePickerFormItem";
import * as dateUtils from "../utils/date-utils";
import {MOMENT_DATE_TIME_FORMAT} from "../utils/date-utils";
import NumberFormItem from "../common/form-items/NumberFormItem";
import {numberGroupFormatter} from "../utils/number-utils";
import {notBlankValidator, phoneSuffix} from "../utils/form-utils";
import RadioGroupFormItem from "../common/form-items/RadioGroupFormItem";
import TextAreaFormItem from "../common/form-items/TextAreaFormItem";
import SectionHeaderFormItem from "../common/form-items/SectionHeaderFormItem";
import {RadioChangeEvent} from "antd/lib/radio/interface";
import {costBearerOptions, CostCalcArgs, maintenanceValues} from "./MaintenanceTypes";
import AutoCompleteFormItem from "../common/form-items/AutoCompleteFormItem";
import {ClientAndSiteSelector} from "./ClientAndSiteSelector";
import FileUploadEditor from "../common/input/FileUploadEditor";
import furoApi from "../api/FuroApi";
import MaintenanceEntryEditor from "./MaintenanceEntryEditor";
import HiddenFormItem from "../common/form-items/HiddenFormItem";
import ItemSelector from "../item/ItemSelector";
import {RentalState} from "../common/state";
import ItemDetailsBox from "../checklist/ItemDetailsBox";
import StaticFormItem from "../common/form-items/StaticFormItem";
import InvoiceCell from "../invoicing/InvoiceCell";
import {InvoiceState} from "../invoicing/invoice";

const FIELD_ITEM = 'item';
const FIELD_DATE = 'date';
const FIELD_NAME = 'name';
const FIELD_OPERATION_HOURS = 'operationHours';
const FIELD_TOTAL_COST = 'totalCost';
const FIELD_COST_BEARER = 'costBearer';
const FIELD_TRAVEL_DISTANCE = 'travelDistance';
const FIELD_TRAVEL_COST = 'travelCost';
const FIELD_REPAIR_TIME = 'repairTime';
const FIELD_REPAIR_COST = 'repairCost';
const FIELD_WORKER_NAMES = "workerNames"
const FIELD_CONTACT_NAME = 'contactName';
const FIELD_CONTACT_PHONE = 'contactPhone';
const FIELD_ERROR_DESCRIPTION = "errorDescription";
const FIELD_WORK_DESCRIPTION = "workDescription";
const FIELD_COMMENT = "comment";

interface Props extends FormComponentProps {
  editable: boolean;
  historyItem: MaintenanceHistory;
  types: string[];
  onFormSubmit: (item: MaintenanceHistory) => void;
  onFormCancel: () => void;
  onAttachmentChange?: () => void;
  onHistoryItemReload?: () => void;
}

interface State {
  selectedItem?: Item;
  itemSelectorVisible: boolean;
  initialTravelDistance: number;
  initialTravelCost: number;
  initialRepairTime: number;
  initialRepairCost: number;
  totalCost: number;
  entryAmountTotal: number;
  entries: MaintenanceHistoryItemEntry[];
  client?: ClientSummary;
  site?: Site;
  renterCost: boolean;
  typeDataSource: string[],
}

class MaintenanceHistoryForm extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    const {types, historyItem} = props;
    const {
      costBearer,
      travelDistance,
      travelCost,
      repairTime,
      repairCost,
      entries,
      totalCost,
      client,
      site
    } = historyItem;

    const cb = costBearer || COST_BEARER_RENTER;
    const defaultValues = maintenanceValues[cb]!!;

    this.state = {
      renterCost: cb === COST_BEARER_RENTER,
      entries,
      entryAmountTotal: calculateEntriesTotal(entries),
      totalCost: totalCost ?? 0,
      initialTravelDistance: travelDistance ?? defaultValues.defaultTravelDistance,
      initialTravelCost: travelCost ?? defaultValues.defaultTravelCost,
      initialRepairTime: repairTime ?? defaultValues.defaultRepairTime,
      initialRepairCost: repairCost ?? defaultValues.defaultRepairCost,
      typeDataSource: types,
      client,
      site,
      selectedItem: historyItem.item,
      itemSelectorVisible: false,
    };
  }

  public componentDidUpdate(prevProps: Props) {
    if (prevProps.historyItem !== this.props.historyItem) {
      const entries = this.props.historyItem.entries || [];
      this.setState({
        entries,
        totalCost: this.props.historyItem.totalCost ?? 0,
        entryAmountTotal: calculateEntriesTotal(entries),
      });
    }
  }

  public render() {
    const {form, historyItem, editable} = this.props;
    const {
      typeDataSource,
      client,
      site,
      totalCost,
      renterCost,
      entries,
      initialTravelDistance,
      initialTravelCost,
      initialRepairTime,
      initialRepairCost,
      selectedItem,
      itemSelectorVisible,
    } = this.state;
    const date = (historyItem && historyItem.date) ? dateUtils.toMomentOrThrow(historyItem.date) : moment();
    const createMode = historyItem.id === null;
    const disabled = !editable;
    const action = historyItem.id === null
      ? undefined
      : furoApi.maintenance.uploadMaintenanceHistoryAttachmentUrl(historyItem.id);

    return (
      <>
        <Form>
          {historyItem?.invoice && historyItem.invoice.state !== InvoiceState.DELETED &&
              <StaticFormItem
                  label={locales.invoice}
                  initialValue={historyItem?.invoice && (
                    <InvoiceCell invoice={historyItem.invoice} finalized={true}/>
                  )}
              />
          }

          <HiddenFormItem
            form={form}
            fieldId={FIELD_ITEM}
            label={locales.item}
            initialValue={undefined}
            required={false}
          >
            {!selectedItem && editable && (
              <Button icon="search" onClick={this.openItemSelector}>
                {locales.select_item}
              </Button>
            )}
            {!selectedItem && !editable && (
              '-'
            )}
            {selectedItem && (
              <ItemDetailsBox
                item={selectedItem}
                closable={historyItem.item === undefined}
                onClose={this.onItemCleared}
              />
            )}
          </HiddenFormItem>
          <DatePickerFormItem
            form={form}
            fieldId={FIELD_DATE}
            label={locales.maintenance_date}
            initialValue={date}
            required={true}
            showTime={true}
            format={MOMENT_DATE_TIME_FORMAT}
            disabled={disabled}
          />

          <AutoCompleteFormItem
            form={form}
            fieldId={FIELD_NAME}
            label={locales.appellation}
            initialValue={historyItem.name}
            required={true}
            dataSource={typeDataSource}
            disabled={disabled}
            validateOnDataSource={false}
          />

          <InputFormItem
            form={form}
            fieldId={FIELD_WORKER_NAMES}
            label={locales.worker_names}
            initialValue={historyItem.workerNames}
            required={true}
            disabled={disabled}
            rules={[{
              message: locales.cant_be_empty,
              validator: (_, value, callback) => notBlankValidator(value, callback),
            }]}
          />

          <RadioGroupFormItem
            form={form}
            fieldId={FIELD_COST_BEARER}
            label={locales.cost_bearer}
            initialValue={historyItem.costBearer}
            required={true}
            options={costBearerOptions}
            onChange={this.onCostBearerChange}
            disabled={!createMode}
          />

          <TextAreaFormItem
            form={form}
            fieldId={FIELD_ERROR_DESCRIPTION}
            label={locales.error_description}
            initialValue={historyItem.errorDescription}
            maxLength={255}
            required={false}
            disabled={disabled}
          />

          <TextAreaFormItem
            form={form}
            fieldId={FIELD_WORK_DESCRIPTION}
            label={locales.work_description}
            initialValue={historyItem.workDescription}
            maxLength={255}
            required={true}
            disabled={disabled}
          />

          <TextAreaFormItem
            form={form}
            fieldId={FIELD_COMMENT}
            label={locales.comment}
            initialValue={historyItem.comment}
            maxLength={255}
            required={false}
            disabled={disabled}
          />

          <SectionHeaderFormItem title={locales.client}/>

          <ClientAndSiteSelector
            form={form}
            createMode={createMode}
            onClientChange={this.onClientChange}
            onSiteChange={this.onSiteChange}
            initialClient={historyItem.client}
            initialSite={historyItem.site}
            clientRequired={renterCost}
            client={client}
            site={site}
          />

          <InputFormItem
            form={form}
            fieldId={FIELD_CONTACT_NAME}
            label={locales.contact_name}
            initialValue={historyItem.contactName}
            required={renterCost}
            disabled={disabled}
            rules={[{
              validator: renterCost
                ? (_, value, callback) => notBlankValidator(value, callback)
                : undefined,
            }]}
          />

          <InputFormItem
            form={form}
            fieldId={FIELD_CONTACT_PHONE}
            label={locales.contact_phone}
            initialValue={historyItem.contactPhone}
            type="tel"
            required={renterCost}
            disabled={disabled}
            addonAfter={historyItem.contactPhone && phoneSuffix(historyItem.contactPhone)}
            rules={[{
              validator: renterCost
                ? (_, value, callback) => notBlankValidator(value, callback)
                : undefined,
            }]}
          />

          <SectionHeaderFormItem title={locales.operationHours}/>

          <NumberFormItem
            label={locales.operation_hours_reading}
            style={{fontWeight: "bold"}}
            fieldId={FIELD_OPERATION_HOURS}
            form={form}
            initialValue={historyItem ? historyItem.operationHours : null}
            required={false}
            min={0}
            step={0.1}
            precision={1}
            formatter={numberGroupFormatter}
            disabled={disabled}
          >
            <> {locales.hours}</>
          </NumberFormItem>

          <SectionHeaderFormItem title={locales.travel}/>

          <NumberFormItem
            label={locales.distance}
            fieldId={FIELD_TRAVEL_DISTANCE}
            form={form}
            initialValue={initialTravelDistance}
            required={true}
            min={0}
            step={1}
            precision={0}
            formatter={numberGroupFormatter}
            onChange={this.onTravelDistanceChange}
            disabled={disabled}
          >
            <> km</>
          </NumberFormItem>

          <NumberFormItem
            label={locales.fee_per_km}
            fieldId={FIELD_TRAVEL_COST}
            form={form}
            initialValue={initialTravelCost}
            required={true}
            min={0}
            step={1}
            precision={0}
            formatter={numberGroupFormatter}
            onChange={this.onTravelCostChange}
            disabled={disabled}
          >
            <> {locales.app_currency}</>
          </NumberFormItem>

          <SectionHeaderFormItem title={locales.repair}/>

          <NumberFormItem
            label={locales.repair_time}
            fieldId={FIELD_REPAIR_TIME}
            form={form}
            initialValue={initialRepairTime}
            required={true}
            min={0}
            step={0.25}
            precision={2}
            hideIntegerDecimals={true}
            formatter={numberGroupFormatter}
            onChange={this.onRepairTimeChange}
            disabled={disabled}
          >
            <> {locales.hours}</>
          </NumberFormItem>

          <NumberFormItem
            label={locales.repair_cost_per_hour}
            fieldId={FIELD_REPAIR_COST}
            form={form}
            initialValue={initialRepairCost}
            required={true}
            min={0}
            step={1}
            formatter={numberGroupFormatter}
            onChange={this.onRepairCostChange}
            disabled={disabled}
          >
            <> {locales.app_currency}</>
          </NumberFormItem>

          <SubmitCancelFormItem disabled={disabled} onSubmit={this.onFormSubmit} onCancel={this.onFormCancel}/>

          <SectionHeaderFormItem title={locales.material_usage}/>

          <MaintenanceEntryEditor
            disabled={createMode || historyItem.finalized}
            renterCost={renterCost}
            entries={entries}
            item={historyItem.item}
            onEntriesToAdd={this.onEntriesToAdd}
            onEntryDelete={this.onEntryDelete}
            onEntryUpdate={this.onEntryUpdate}
          />

          <NumberFormItem
            style={{marginTop: 40}}
            label={locales.total_cost}
            fieldId={FIELD_TOTAL_COST}
            form={form}
            initialValue={totalCost}
            required={false}
            disabled={true}
            formatter={numberGroupFormatter}
          >
            <> {locales.app_currency}</>
          </NumberFormItem>

          {
            this.props.onAttachmentChange &&
              <>
                  <SectionHeaderFormItem title={locales.attachments}/>
                  <FileUploadEditor
                      disabled={createMode || historyItem.finalized}
                      itemId={historyItem.id!}
                      onAttachmentChange={this.props.onAttachmentChange}
                      action={action}
                      loadAttachment={this.loadAttachment}
                      deleteAttachment={this.deleteAttachment}
                  />
              </>
          }
        </Form>

        {itemSelectorVisible && (
          <Modal
            visible={true}
            title={locales.select_item}
            footer={null}
            onCancel={this.closeItemSelector}
            width="auto"
            style={{margin: '0 24px'}}
            destroyOnClose={true}
          >
            <ItemSelector
              allowedStates={[RentalState.RENTED, RentalState.AVAILABLE]}
              selectedStates={[RentalState.RENTED, RentalState.AVAILABLE]}
              excludedFromSearch={[]}
              onSelect={this.onItemSelected}
            />
          </Modal>
        )}
      </>
    );
  }

  private openItemSelector = () => this.setState({itemSelectorVisible: true});

  private closeItemSelector = () => this.setState({itemSelectorVisible: false});

  private onItemSelected = (item: Item) => {
    this.setState({selectedItem: item, itemSelectorVisible: false}, () => {
      this.props.form.setFieldsValue({[FIELD_ITEM]: item});
    });
  };

  private onItemCleared = () => {
    this.setState({selectedItem: undefined}, () => {
      this.props.form.setFieldsValue({[FIELD_ITEM]: undefined});
      this.props.form.validateFields([FIELD_ITEM], {force: true}, () => undefined);
    });
  };

  private loadAttachment = (): Promise<Attachment[]> => {
    const id = this.props.historyItem.id;
    if (!id) {
      return Promise.resolve([]);
    }
    return furoApi.maintenance.getMaintenanceHistoryAttachments(id);
  }

  private deleteAttachment = (attachmentId: number): Promise<void> => {
    const id = this.props.historyItem.id;
    if (!id) {
      return Promise.resolve();
    }
    return furoApi.maintenance.deleteMaintenanceHistoryAttachment(id, attachmentId);
  }

  private onClientChange = (client?: ClientSummary) => {
    this.setState({client});
  };

  private onSiteChange = (site?: Site) => {
    this.setState({site});
  };

  private calculateTotal = (args: CostCalcArgs = {}) => {
    const {form} = this.props;
    const {entryAmountTotal} = this.state;

    const travelDistance = args.travelDistance ?? form.getFieldValue(FIELD_TRAVEL_DISTANCE);
    const travelCost = args.travelCost ?? form.getFieldValue(FIELD_TRAVEL_COST);
    const repairTime = args.repairTime ?? form.getFieldValue(FIELD_REPAIR_TIME);
    const repairCost = args.repairCost ?? form.getFieldValue(FIELD_REPAIR_COST);

    const travelSum = travelCost * travelDistance;
    const repairSum = repairCost * repairTime;
    const totalCost = travelSum + repairSum + entryAmountTotal;

    form.setFieldsValue({
      [FIELD_TOTAL_COST]: totalCost,
    });

    this.setState({totalCost});
  }

  private onCostBearerChange = (e: RadioChangeEvent) => {
    const {historyItem, form} = this.props;
    const cb = e.target.value || COST_BEARER_RENTER;

    if (historyItem.id == null) {
      // create mode
      const defaultValues = maintenanceValues[cb]!!;
      form.setFieldsValue({
        [FIELD_TRAVEL_DISTANCE]: defaultValues.defaultTravelDistance,
        [FIELD_TRAVEL_COST]: defaultValues.defaultTravelCost,
        [FIELD_REPAIR_TIME]: defaultValues.defaultRepairTime,
        [FIELD_REPAIR_COST]: defaultValues.defaultRepairCost,
      })
      this.calculateTotal();
    }

    const contactRequired = cb === COST_BEARER_RENTER;
    this.setState({
      renterCost: contactRequired,
    }, () => {
      if (!contactRequired) {
        form.validateFields([FIELD_CONTACT_NAME, FIELD_CONTACT_PHONE]);
      }
    })
  }

  private onTravelDistanceChange = (value: number | undefined) => {
    this.calculateTotal({travelDistance: value});
  }

  private onTravelCostChange = (value: number | undefined) => {
    this.calculateTotal({travelCost: value});
  }

  private onRepairTimeChange = (value: number | undefined) => {
    this.calculateTotal({repairTime: value});
  }

  private onRepairCostChange = (value: number | undefined) => {
    this.calculateTotal({repairCost: value});
  }

  private onEntriesToAdd = (entriesToAdd: MaintenanceHistoryItemEntry[]) => {
    if (entriesToAdd.length === 0) {
      return;
    }

    const {historyItem} = this.props;
    if (historyItem.id == null) {
      const entries = [...this.state.entries, ...entriesToAdd];
      this.setState({entries, entryAmountTotal: calculateEntriesTotal(entries)}, () => this.calculateTotal());
      return;
    }

    const entriesToCreate = entriesToAdd.map(e => ({
      inventoryStockItemId: e.inventoryStockItem?.id,
      infiniteInventoryStockId: e.infiniteInventoryStockId,
      infiniteInventoryStockAmount: e.amount
    }))

    furoApi.maintenance.addMaintenanceHistoryEntries(historyItem.id, entriesToCreate)
      .then(this.reloadHistoryItem)
      .catch(() => undefined);
  };

  private onEntryDelete = (entry: MaintenanceHistoryItemEntry) => {
    const {historyItem} = this.props;

    if (historyItem.id == null || entry.id == null) {
      const entries = this.state.entries.filter(e => !isSameEntry(e, entry));
      this.setState({entries, entryAmountTotal: calculateEntriesTotal(entries)}, () => this.calculateTotal());
      return;
    }

    furoApi.maintenance.deleteMaintenanceHistoryEntry(historyItem.id, entry.id)
      .then(this.reloadHistoryItem)
      .catch(() => undefined);
  };

  private onEntryUpdate = (entry: MaintenanceHistoryItemEntry) => {
    const {historyItem} = this.props;

    if (historyItem.id == null || entry.id == null) {
      const entries = this.state.entries.map(e => isSameEntry(e, entry) ? entry : e);
      this.setState({entries, entryAmountTotal: calculateEntriesTotal(entries)}, () => this.calculateTotal());
      return;
    }

    const request = {
      cost: Number(entry.cost.toFixed()),
      discount: entry.discount ? Number(entry.discount.toFixed()) : undefined,
    }

    furoApi.maintenance.saveMaintenanceHistoryEntryCost(historyItem.id, entry.id, request)
      .then(this.reloadHistoryItem)
      .catch(() => undefined);
  };

  private reloadHistoryItem = () => {
    if (this.props.onHistoryItemReload) {
      this.props.onHistoryItemReload();
      return;
    }

    const entries = this.props.historyItem.entries;
    this.setState({entries, entryAmountTotal: calculateEntriesTotal(entries)}, () => this.calculateTotal());
  };

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onFormSubmit = () => {
    const {historyItem, form, onFormSubmit} = this.props;
    const {client, site} = this.state;

    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onFormSubmit({
          ...historyItem,
          item: values[FIELD_ITEM],
          date: values[FIELD_DATE],
          name: values[FIELD_NAME],
          operationHours: values[FIELD_OPERATION_HOURS],
          totalCost: this.state.totalCost,
          costBearer: values[FIELD_COST_BEARER],
          workerNames: values[FIELD_WORKER_NAMES],
          errorDescription: values[FIELD_ERROR_DESCRIPTION],
          workDescription: values[FIELD_WORK_DESCRIPTION],
          comment: values[FIELD_COMMENT],
          client,
          site,
          travelDistance: values[FIELD_TRAVEL_DISTANCE],
          travelCost: values[FIELD_TRAVEL_COST],
          repairTime: values[FIELD_REPAIR_TIME],
          repairCost: values[FIELD_REPAIR_COST],
          contactName: values[FIELD_CONTACT_NAME],
          contactPhone: values[FIELD_CONTACT_PHONE],
        });
      }
    });
  };
}

export default Form.create<Props>()(MaintenanceHistoryForm);

const calculateEntriesTotal = (entries: MaintenanceHistoryItemEntry[]): number => {
  return entries.map(e => Number((e.cost * e.amount * (1 - (e.discount ?? 0)/ 100)).toFixed())).reduce((a, b) => a + b, 0);
};

const isSameEntry = (a: MaintenanceHistoryItemEntry, b: MaintenanceHistoryItemEntry): boolean => {
  if (a.id != null && b.id != null) {
    return a.id === b.id;
  }
  return a.inventoryStockItem === b.inventoryStockItem
    && a.infiniteInventoryStockId === b.infiniteInventoryStockId
    && a.inventoryNumber === b.inventoryNumber
    && a.name === b.name
    && a.amount === b.amount
    && a.originalCost === b.originalCost;
};




// WEBPACK FOOTER //
// ./src/maintenance/MaintenanceHistoryForm.tsx